import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Stack,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

export default function EditWillLead() {
  const { id } = useParams();
  const navigate = useNavigate();
  const authCtx = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
  const [groupCodes, setGroupCodes] = React.useState([]);
  const [partners, setPartners] = React.useState([]);
  const [lenderPanels, setLenderPanels] = React.useState([]);
  const [leadData, setLeadData] = React.useState(null);
  const [isPostcodeValid, setIsPostcodeValid] = React.useState(false);
  const [postcodeError, setPostcodeError] = React.useState('');
  const [postcodeMatch, setPostcodeMatch] = React.useState(null);
  const [checkingPostcode, setCheckingPostcode] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      group_code: '',
      lender_panel_id: '',
      partner_id: '',
      acquisition: 'Inbound',
      service_type: '',
      about: [],
      approximate_value_of_assets: '',
      address1: '',
      address2: '',
      town: '',
      country: '',
      postcode: ''
    }
  });

  React.useEffect(() => {
    if (authCtx.token && id) {
      fetchGroupCodes();
      fetchLenderPanels();
      fetchPartners();
      fetchLeadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authCtx.token]);

  const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setGroupCodes(res.data);
      }
    } catch (err) {
      console.error('Error fetching group codes:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const res = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (res?.status === 200 && res?.data) {
        setLenderPanels(res.data);
      }
    } catch (err) {
      console.error('Error fetching lender panels:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setPartners(res.data.docs);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  };

  const aboutOptions = [
    {
      label: "Do you wish to leave the whole of your estate to your spouse/civil partner?",
      helpText: ""
    },
    {
      label: "Do you own more than one property?",
      helpText: "We recommend that you discuss your options on how you can protect the property from the potential risk of care home fees or in case of a divorce in the family."
    },
    {
      label: "Do you wish to leave any assets to your grandchildren or minor children?",
      helpText: "You may benefit from further advice on Will trusts to ensure that your beneficiaries will receive their inheritance when they are mature enough."
    },
    {
      label: "Do you wish to exclude any of your relatives from your Will?",
      helpText: "Our experts will advise you on how you could reduce the risk of your Will being challenged by disinherited relatives."
    },
    {
      label: "Do you have any Foreign Assets?",
      helpText: ""
    },
    {
      label: "Will there be more than 5 beneficiaries?",
      helpText: ""
    },
    {
      label: "Would you like to leave the right to usage of your property to someone?",
      helpText: ""
    }
  ];

  const fetchLeadData = async () => {
    setLoading(true);
    try {
      const response = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);
      const lead = response?.data;

      if (lead) {
        setLeadData(lead);

        console.log('📋 Lead Data:', lead);
        console.log('📝 Lead About:', lead.about);
        console.log('📝 About type:', typeof lead.about);
        console.log('📝 Is Array?:', Array.isArray(lead.about));

        // Convert about object to array format
        let aboutArray = [];

        // Check if about is already an array (new format)
        if (Array.isArray(lead.about)) {
          aboutArray = lead.about;
          console.log('✅ Using array format');
        }
        // Check if about is an object (old format)
        else if (lead.about && typeof lead.about === 'object') {
          console.log('🔄 Converting object format to array');
          if (lead.about.leave_whole_estate) aboutArray.push(aboutOptions[0].label);
          if (lead.about.own_more_than_one_property) aboutArray.push(aboutOptions[1].label);
          if (lead.about.leave_assets_to_grandchildren) aboutArray.push(aboutOptions[2].label);
          if (lead.about.exclude_relatives) aboutArray.push(aboutOptions[3].label);
          if (lead.about.have_foreign_assets) aboutArray.push(aboutOptions[4].label);
          if (lead.about.more_than_5_beneficiaries) aboutArray.push(aboutOptions[5].label);
          if (lead.about.leave_right_to_usage) aboutArray.push(aboutOptions[6].label);
        } else {
          console.log('⚠️ About field is neither array nor object:', lead.about);
        }

        console.log('✅ Final About Array to set in form:', aboutArray);
        console.log('✅ About Array length:', aboutArray.length);

        const formValues = {
          group_code: lead.group_code?._id ? String(lead.group_code._id) : '',
          lender_panel_id: lead.lender_panel_id?._id ? String(lead.lender_panel_id._id) : '',
          partner_id: lead.partner_id?._id ? String(lead.partner_id._id) : '',
          acquisition: lead.acquisition || 'Inbound',
          service_type: lead.service_type || 'single',
          about: aboutArray,
          approximate_value_of_assets: lead.approximate_value_of_assets || '',
          address1: lead.address?.address1 || '',
          address2: lead.address?.address2 || '',
          town: lead.address?.town || '',
          country: lead.address?.country || '',
          postcode: lead.address?.postcode || ''
        };

        console.log('🎯 Resetting form with values:', formValues);
        console.log('🎯 About field value being set:', formValues.about);

        reset(formValues);

        console.log('✅ Form reset complete');
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
      Swal.fire('Error', 'Failed to fetch lead data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Validate postcode and match with branch
  const validatePostcode = async (postcodeValue) => {
    console.log('🔍 Validating postcode:', postcodeValue);
    setCheckingPostcode(true);

    try {
      // Reset states
      setPostcodeMatch(null);
      setIsPostcodeValid(false);
      setPostcodeError('');

      // STEP 1: Validate postcode exists in UK database
      console.log('📤 Step 1: Validating postcode with UK database');
      const response = await postRequest('private/validatePostcode', { postcode: postcodeValue }, authCtx.token);
      console.log('📡 Step 1 Response:', response);

      // Check if postcode is INVALID
      if (response.success !== true || !response.data) {
        console.log('❌ Step 1 Failed - Invalid postcode');
        setIsPostcodeValid(false);
        setPostcodeError('Invalid UK postcode');
        Swal.fire({
          title: 'Invalid Postcode',
          text: 'Please enter a valid UK postcode. This postcode does not exist in our database.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        setCheckingPostcode(false);
        return false;
      }

      // STEP 2: Match postcode with branch
      console.log('✅ Step 1 Passed - Valid postcode');
      console.log('🔍 Step 2: Matching postcode with branch');
      const matchRes = await postRequest(
        'private/matchBranchByPostcode',
        { postcode: postcodeValue.trim() },
        authCtx.token
      );
      console.log('📡 Step 2 Response:', matchRes);

      if (matchRes?.data?.matched) {
        // ✅ Branch matched
        console.log('✅ Step 2 Passed - Branch matched:', matchRes.data.branch);
        setPostcodeMatch(matchRes.data.branch);
        setIsPostcodeValid(true);
        setPostcodeError('');
        setCheckingPostcode(false);
        return true;
      } else {
        // ❌ No branch matched
        console.log('❌ Step 2 Failed - No branch matched');
        setIsPostcodeValid(false);
        setPostcodeError('No branch available for this postcode');
        Swal.fire({
          title: 'Branch Not Available',
          text: matchRes?.message || 'No branch is available for this postcode area.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        setCheckingPostcode(false);
        return false;
      }

    } catch (error) {
      console.error('❌ Postcode validation error:', error);
      setIsPostcodeValid(false);
      setPostcodeError('Could not validate postcode');
      Swal.fire({
        title: 'Validation Error',
        text: 'Could not validate postcode. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setCheckingPostcode(false);
      return false;
    }
  };

  const onSubmit = async (formData) => {
    // Validate postcode before submission
    if (formData.postcode) {
      const postcodeValue = formData.postcode.trim();
      const isValid = await validatePostcode(postcodeValue);
      if (isValid === false) {
        console.log('❌ Form submission blocked - Invalid postcode');
        return; // Stop form submission
      }
    }
    const created_id = localStorage.getItem('id');

    const payload = {
      group_code: formData.group_code,
      lender_panel_id: formData.lender_panel_id || null,
      partner_id: formData.partner_id || null,
      acquisition: formData.acquisition,
      service_type: formData.service_type,
      approximate_value_of_assets: formData.approximate_value_of_assets,
      about: formData.about,
      address: {
        address1: formData.address1,
        address2: formData.address2,
        town: formData.town,
        country: formData.country,
        postcode: formData.postcode
      },
      product_id: leadData?.product_id?._id,
      created_id: created_id,
      branch_id: postcodeMatch?._id || null // Add matched branch ID
    };

    console.log('Edit Payload:', payload);
    console.log('📌 Matched Branch:', postcodeMatch);

    setLoading(true);
    try {
      const res = await postRequest(`private/lead-edit/${id}`, payload, authCtx.token);
      if (res?.status === 'success' || res?.status === 200) {
        Swal.fire('Success', 'Will lead updated successfully!', 'success');
        navigate(`/leads/view/will/${id}`);
      } else {
        Swal.fire('Error', res?.message || 'Update failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `Edit Will #${id}`, path: '' },
        ]}
      />
      <br />

      <div className="page-header pb-3">
        <h1 className="mt-0 mb-0">Edit Will #{id}</h1>
      </div>

      <div className="page-top-formview mb-3 edit-lead">
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h5 className="mb-4">Edit Will #{id}</h5>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="group_code">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="group-code-label">Group Code</InputLabel>
                  <Controller
                    name="group_code"
                    control={control}
                    rules={{ required: 'Group Code is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="group-code-label"
                        id="group-code-select"
                        label="Group Code"
                        value={field.value}
                      >
                        {groupCodes.map((item) => (
                          <MenuItem key={item._id} value={String(item._id)}>
                            {`${item.name} (${item.code})`}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.group_code && (
                    <p style={{ color: 'red', fontSize: '0.8rem' }}>{errors.group_code.message}</p>
                  )}
                </FormControl>
              </Form.Group>

              <Form.Group as={Col} md={6} controlId="acquisition">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="acquisition-label">Acquisition</InputLabel>
                  <Controller
                    name="acquisition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="acquisition-label"
                        id="acquisition-select"
                        label="Acquisition"
                      >
                        <MenuItem value="Inbound">Inbound</MenuItem>
                        <MenuItem value="Outbound">Outbound</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="lender_panel_id">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="lender-panel-label">Lender Panel</InputLabel>
                  <Controller
                    name="lender_panel_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="lender-panel-label"
                        id="lender-panel-select"
                        label="Lender Panel"
                        value={field.value}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={String(panel._id)}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>

              <Form.Group as={Col} md={6} controlId="partner_id">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="partner-label">Partner</InputLabel>
                  <Controller
                    name="partner_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="partner-label"
                        id="partner-select"
                        label="Partner"
                        value={field.value}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={String(partner._id)}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <h6 className="mb-3 mt-4">Customer Address</h6>
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="address1">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="address1"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="address1"
                        label="Address"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="address2">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="address2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="address2"
                        label="Address2"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={4} controlId="town">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="town"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="town"
                        label="Town"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="country">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="country"
                        label="Country"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="postcode">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="postcode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="postcode"
                        label="PostCode"
                        variant="outlined"
                        fullWidth
                        onBlur={async (e) => {
                          field.onBlur();
                          const postcodeValue = e.target.value.trim();
                          if (postcodeValue) {
                            console.log('🔍 OnBlur - Validating postcode:', postcodeValue);
                            await validatePostcode(postcodeValue);
                          } else {
                            setIsPostcodeValid(false);
                            setPostcodeError('');
                            setPostcodeMatch(null);
                          }
                        }}
                        onChange={(e) => {
                          field.onChange(e);
                          if (postcodeError) {
                            setPostcodeError('');
                            setIsPostcodeValid(false);
                            setPostcodeMatch(null);
                          }
                        }}
                        error={!!postcodeError}
                        helperText={
                          checkingPostcode
                            ? 'Validating postcode...'
                            : postcodeError
                            ? postcodeError
                            : isPostcodeValid && postcodeMatch
                            ? `✓ Valid postcode - Branch: ${postcodeMatch.name}`
                            : ''
                        }
                        disabled={checkingPostcode}
                        sx={{
                          '& .MuiFormHelperText-root.Mui-error': {
                            color: '#d32f2f'
                          },
                          '& .MuiFormHelperText-root:not(.Mui-error)': {
                            color: isPostcodeValid ? '#2e7d32' : 'inherit'
                          }
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="service_type">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="type-of-service-label">Type of Service</InputLabel>
                  <Controller
                    name="service_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="type-of-service-label"
                        id="type-of-service-select"
                        label="Type of Service"
                        value={field.value}
                      >
                        <MenuItem value="single">Single</MenuItem>
                        <MenuItem value="mirror">Mirror</MenuItem>
                        <MenuItem value="codicil">Codicil</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Controller
                  name="about"
                  control={control}
                  render={({ field }) => {
                    console.log('🔍 Rendering checkboxes with field.value:', field.value);
                    // Ensure field.value is always an array
                    const currentValue = Array.isArray(field.value) ? field.value : [];
                    return (
                      <FormGroup>
                        {aboutOptions.map((option) => {
                          const isChecked = currentValue.includes(option.label);
                          console.log(`🔍 Checkbox "${option.label}" checked:`, isChecked);
                          return (
                            <div key={option.label} style={{ display: 'block', marginBottom: '16px' }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const newValue = e.target.checked
                                        ? [...currentValue, option.label]
                                        : currentValue.filter((val) => val !== option.label);
                                      console.log('📝 Checkbox changed, new value:', newValue);
                                      field.onChange(newValue);
                                    }}
                                    sx={{
                                      '&.Mui-checked': {
                                        color: '#764ba2',
                                      },
                                    }}
                                  />
                                }
                                label={option.label}
                              />
                              {option.helpText && isChecked && (
                                <div style={{
                                  marginLeft: '32px',
                                  marginTop: '4px',
                                  color: '#666',
                                  fontSize: '0.9rem',
                                  lineHeight: '1.4'
                                }}>
                                  {option.helpText}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </FormGroup>
                    );
                  }}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="approximate_value_of_assets">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="approximate-value-label">Approximate Value of Assets</InputLabel>
                  <Controller
                    name="approximate_value_of_assets"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="approximate-value-label"
                        id="approximate-value-select"
                        label="Approximate Value of Assets"
                      >
                        <MenuItem value="Upto £500,000">Upto £500,000</MenuItem>
                        <MenuItem value="£500,000 - £1,000,000">£500,000 - £1,000,000</MenuItem>
                        <MenuItem value="Over £1,000,000">Over £1,000,000</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Stack direction="row" spacing={2} className="mt-4">
              <Button
                variant="outlined"
                onClick={() => navigate('/leads')}
                className="red-outline-btn text-capitalize"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="red-bg red-button text-capitalize"
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
              </Button>
            </Stack>
          </Form>
        </div>
      </div>
    </>
  );
}
